import { useEffect, useRef } from "react";
import type { ArchDesc, Topology } from "../api/types";

const NS = "http://www.w3.org/2000/svg";

function el(tag: string, attrs: Record<string, string | number>): SVGElement {
  const e = document.createElementNS(NS, tag);
  for (const k in attrs) e.setAttribute(k, String(attrs[k]));
  return e;
}
function edgeStyle(w: number, gmax: number): { col: string; op: number; sw: number } | null {
  const a = Math.abs(w) / gmax;
  if (a < 0.06) return null;
  return { col: w >= 0 ? "#4a9eff" : "#ff5a5a", op: Math.min(0.85, 0.06 + a * 0.8), sw: +(0.4 + a * 2.0).toFixed(2) };
}
function ys(n: number, top: number, bot: number): number[] {
  return Array.from({ length: n }, (_, i) => (n === 1 ? (top + bot) / 2 : top + ((bot - top) * i) / (n - 1)));
}

// CTRNN1: solve H from genome length, lay out input→interneurons→output.
function drawCtrnn1(svg: SVGSVGElement, genome: number[], I: number, O: number): string {
  svg.setAttribute("viewBox", "0 0 480 360");
  const L = genome.length;
  const b = I + O + 2;
  const H = Math.round((-b + Math.sqrt(b * b - 4 * (O - L))) / 2);
  if (H < 1 || H * H + b * H + O !== L) return `unknown encoding (len ${L})`;
  const oWin = 0, oWrec = H * I, oBh = oWrec + H * H, oTau = oBh + H, oWout = oTau + H, oBout = oWout + O * H;
  const win = (i: number, h: number) => genome[oWin + h * I + i];
  const wrec = (j: number, h: number) => genome[oWrec + h * H + j];
  const wout = (o: number, h: number) => genome[oWout + o * H + h];
  const gmax = (lo: number, hi: number) => { let m = 1e-6; for (let k = lo; k < hi; k++) m = Math.max(m, Math.abs(genome[k])); return m; };
  const maxIn = gmax(oWin, oWrec), maxRec = gmax(oWrec, oBh), maxOut = gmax(oWout, oBout);
  const X = { in: 70, hid: 240, out: 410 };
  const top = 34, bot = 338;
  const iy = ys(I, top, bot), hy = ys(H, top, bot), oy = ys(O, top, bot);
  const R = H > 10 ? 5.5 : 7;

  const edge = (x1: number, y1: number, x2: number, y2: number, w: number, gm: number, curve?: string) => {
    const s = edgeStyle(w, gm);
    if (!s) return;
    svg.appendChild(curve
      ? el("path", { d: curve, stroke: s.col, "stroke-width": s.sw, "stroke-opacity": s.op, fill: "none" })
      : el("line", { x1, y1, x2, y2, stroke: s.col, "stroke-width": s.sw, "stroke-opacity": s.op }));
  };
  for (let i = 0; i < I; i++) for (let h = 0; h < H; h++) edge(X.in, iy[i], X.hid, hy[h], win(i, h), maxIn);
  for (let j = 0; j < H; j++) for (let h = 0; h < H; h++) {
    if (j === h) {
      const y = hy[h];
      edge(0, 0, 0, 0, wrec(j, h), maxRec, `M ${X.hid},${y - R} C ${X.hid + 24},${y - 42} ${X.hid - 24},${y - 42} ${X.hid},${y - R}`);
    } else {
      const cx = X.hid + (j < h ? 18 : -18), my = (hy[j] + hy[h]) / 2;
      edge(0, 0, 0, 0, wrec(j, h), maxRec, `M ${X.hid},${hy[j]} C ${cx},${my} ${cx},${my} ${X.hid},${hy[h]}`);
    }
  }
  for (let h = 0; h < H; h++) for (let o = 0; o < O; o++) edge(X.hid, hy[h], X.out, oy[o], wout(o, h), maxOut);

  const node = (x: number, y: number, label: string | null, side: "l" | "r" | "") => {
    svg.appendChild(el("circle", { cx: x, cy: y, r: R, fill: "rgba(255,255,255,0.10)", stroke: "rgba(255,255,255,0.35)", "stroke-width": 0.6 }));
    if (label) {
      const dx = side === "l" ? -(R + 4) : side === "r" ? R + 4 : 0;
      const dy = side ? 0 : R + 9;
      const t = el("text", { x: x + dx, y: y + dy, "text-anchor": side === "l" ? "end" : side === "r" ? "start" : "middle", "dominant-baseline": "central", "font-size": 8, fill: "#9aa6b2", "font-family": "monospace" });
      t.textContent = label;
      svg.appendChild(t);
    }
  };
  const inL = I === 6 ? ["aX", "aY", "aZ", "gX", "gY", "gZ"] : null;
  for (let i = 0; i < I; i++) node(X.in, iy[i], inL ? inL[i] : "in" + i, "l");
  for (let h = 0; h < H; h++) node(X.hid, hy[h], H <= 10 ? "h" + h : null, "");
  for (let o = 0; o < O; o++) node(X.out, oy[o], "m" + o, "r");
  for (const [x, lbl] of [[X.in, "INPUT"], [X.hid, "INTERNEURONS"], [X.out, "OUTPUT"]] as [number, string][]) {
    const t = el("text", { x, y: 14, "text-anchor": "middle", "font-size": 8.5, fill: "#6b7785", "font-family": "monospace" });
    t.textContent = lbl;
    svg.appendChild(t);
  }
  return `${I} in · ${H} interneurons · ${O} out · ${L} weights`;
}

// Multi-layer arch (ctrnn2 etc.): columns from desc.layers, edges from desc.edges + layout.
function drawLayered(svg: SVGSVGElement, genome: number[], desc: ArchDesc, vbW: number,
                     motors?: number[] | null): string {
  const layers = desc.layers;
  svg.setAttribute("viewBox", `0 0 ${vbW} 360`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  const top = 34, bot = 338;
  const colX = layers.map((_, i) => 60 + ((vbW - 120) * i) / Math.max(1, layers.length - 1));
  const byId: Record<string, { x: number; y: number[]; size: number }> = {};
  layers.forEach((L, i) => { byId[L.id] = { x: colX[i], y: ys(L.size, top, bot), size: L.size }; });

  const layout = desc.layout || {};
  const EDGE_FLOOR = 0.02;   // |w|/fieldMax below this → edge not drawn (and node flagged evolved-silent)
  const fieldMax = (f: string) => { const a = layout[f]; if (!a) return 1e-6; let m = 1e-6; for (let k = a[0]; k < a[1]; k++) m = Math.max(m, Math.abs(genome[k])); return m; };
  const wAt = (f: string, to: number, from: number) => { const a = layout[f]; const cols = a[2][1]; return genome[a[0] + to * cols + from]; };
  // Per source-node strongest outgoing edge (|w|/fieldMax). A node that appears here but stays below
  // EDGE_FLOOR is WIRED-BUT-EVOLVED-TO-ZERO — drawn muted/dashed so it's unmistakable from an input the
  // topology never connected (the audit question: is Δt disconnected, or is the viz just not drawing it?).
  const srcMax: Record<string, number> = {};
  const bump = (id: string, i: number, w: number, gm: number) => {
    const k = id + ":" + i; srcMax[k] = Math.max(srcMax[k] ?? 0, Math.abs(w) / gm);
  };
  // dashed = a RECURRENT or FEEDBACK connection (so they're distinguishable from
  // the solid feed-forward edges).
  const drawEdge = (x1: number, y1: number, x2: number, y2: number, w: number, gm: number,
                    curve?: string, dashed?: boolean) => {
    const a = Math.abs(w) / gm;
    if (a < EDGE_FLOOR) return;   // low floor so weak-but-real edges (e.g. freshly-growing vision) fade in
                            // rather than vanish; width/opacity still scale to the field max (honest:
                            // vision reads faint early and bolds as evolution grows it past proprio noise)
    const col = w >= 0 ? "#4a9eff" : "#ff5a5a";
    const attrs: Record<string, string | number> = {
      stroke: col, "stroke-width": +(0.35 + a * 1.7).toFixed(2),
      "stroke-opacity": Math.min(0.9, 0.14 + a * 0.76),
    };
    if (dashed) attrs["stroke-dasharray"] = "4,3";
    svg.appendChild(curve ? el("path", { d: curve, fill: "none", ...attrs }) : el("line", { x1, y1, x2, y2, ...attrs }));
  };
  for (const e of desc.edges) {
    if (!e.field || !layout[e.field] || e.from === e.to) continue;
    const A = byId[e.from], B = byId[e.to];
    if (!A || !B) continue;
    const gm = fieldMax(e.field);
    // from_offset: this edge's source columns start partway into a shared field
    // (e.g. vision occupies cols 10..13 of w_in_h1, after proprioception's 0..9).
    const off = e.from_offset ?? 0;
    if (e.diagonal) {
      // 1:1 readout — only neuron i → output i is live (off-diagonal is masked away).
      const n = Math.min(A.size, B.size);
      for (let i = 0; i < n; i++) { const w = wAt(e.field, i, i + off); bump(e.from, i, w, gm); drawEdge(A.x, A.y[i], B.x, B.y[i], w, gm); }
    } else {
      for (let from = 0; from < A.size; from++) for (let to = 0; to < B.size; to++) {
        const w = wAt(e.field, to, from + off); bump(e.from, from, w, gm);
        if (e.feedback) {
          // FEEDBACK (e.g. efference out→h2) connects the SAME two columns as the forward edge, so a
          // straight line draws right on top of it and vanishes. Bulge it DOWN into a dashed arc so the
          // feedback is visible as its own bundle below the feed-forward one.
          const x1 = A.x, y1 = A.y[from], x2 = B.x, y2 = B.y[to];
          const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
          drawEdge(0, 0, 0, 0, w, gm, `M ${x1},${y1} Q ${mx},${my + 46} ${x2},${y2}`, true);
        } else {
          drawEdge(A.x, A.y[from], B.x, B.y[to], w, gm, undefined, false);
        }
      }
    }
  }
  for (const L of layers) {
    const f = "w_rec_" + L.id;
    if (!L.recurrent || !layout[f]) continue;
    const A = byId[L.id], gm = fieldMax(f);
    // FULL intra-layer recurrence (w_rec is size×size, all-to-all): self-loops on the
    // diagonal, a left-bulging arc for every cross-connection i←j. All dashed.
    for (let i = 0; i < A.size; i++) for (let j = 0; j < A.size; j++) {
      const w = wAt(f, i, j);                       // w_rec[to=i][from=j]: neuron j → neuron i
      if (i === j) {
        const y = A.y[i];
        drawEdge(0, 0, 0, 0, w, gm,
                 `M ${A.x + 5},${y - 3.5} C ${A.x + 16},${y - 8} ${A.x + 16},${y + 8} ${A.x + 5},${y + 3.5}`, true);
      } else {
        const yj = A.y[j], yi = A.y[i], my = (yj + yi) / 2;
        const bx = A.x - (10 + Math.abs(yi - yj) * 0.16);   // arc to the LEFT of the column
        drawEdge(0, 0, 0, 0, w, gm, `M ${A.x},${yj} C ${bx},${my} ${bx},${my} ${A.x},${yi}`, true);
      }
    }
  }
  layers.forEach((L) => {
    const A = byId[L.id];
    A.y.forEach((y, k) => {
      const r = A.size > 10 ? 5 : 7;
      // WIRED-BUT-SILENT: node is a source of edges but its strongest one is below EDGE_FLOOR →
      // evolution zeroed it out. Dashed hollow ring + dimmed label so it reads as "present, unused"
      // (not "the viz forgot to draw it"). Undefined srcMax = not a source (e.g. motor outputs) → normal.
      const sm = srcMax[L.id + ":" + k];
      const silent = sm !== undefined && sm < EDGE_FLOOR;
      // MOTOR-NEURON ROLE (ctrnn4): nodes at index ≥ reverse_from are the inverted REVERSE-thrust
      // neurons. Colorize the node ring + label GREEN (forward) / ORANGE (reverse) so the two bands
      // are legible at a glance. Only applies to the layer carrying reverse_from (the motor layer).
      const rev = L.reverse_from !== undefined && k >= L.reverse_from;
      const roleCol = L.reverse_from !== undefined ? (rev ? "#e8a020" : "#3fbf6a") : null;
      const roleFill = L.reverse_from !== undefined
        ? (rev ? "rgba(232,160,32,0.18)" : "rgba(63,191,106,0.18)") : null;
      svg.appendChild(el("circle", silent
        ? { cx: A.x, cy: y, r, fill: "rgba(255,255,255,0.02)", stroke: "#5a6572", "stroke-width": 0.8, "stroke-dasharray": "2,2" }
        : roleCol
        ? { cx: A.x, cy: y, r, fill: roleFill!, stroke: roleCol, "stroke-width": 1.2 }
        : { cx: A.x, cy: y, r, fill: "rgba(255,255,255,0.10)", stroke: "rgba(255,255,255,0.35)", "stroke-width": 0.6 }));
      const lab = L.node_labels?.[k];
      if (lab) { const t = el("text", { x: A.x, y: y + 16, "text-anchor": "middle", "font-size": 7, fill: silent ? "#5a6572" : (roleCol ?? "#9aa6b2"), "font-family": "monospace" }); t.textContent = silent ? lab + "·∅" : lab; svg.appendChild(t); }
      // live motor output value to the RIGHT of each motor node — colored by SIGN:
      // GREEN = positive (forward-thrust neuron), ORANGE = negative (reverse-thrust neuron).
      if (L.id === "out" && motors && Number.isFinite(motors[k])) {
        const v = motors[k];
        const col = v >= 0 ? "#3fbf6a" : "#e8a020";   // green positive · orange negative
        const mt = el("text", { x: A.x + 11, y: y + 2.5, "text-anchor": "start", "font-size": 8.5, fill: col, "font-family": "monospace" });
        mt.textContent = v.toFixed(2);
        svg.appendChild(mt);
      }
    });
    const t = el("text", { x: A.x, y: 14, "text-anchor": "middle", "font-size": 8.5, fill: "#6b7785", "font-family": "monospace" });
    t.textContent = L.label;
    svg.appendChild(t);
  });
  return `${desc.name} · ${genome.length} weights`;
}

export function NetworkSvg({ genome, topology, descriptor, motors }: {
  genome: number[] | null;
  topology: Topology | undefined;
  descriptor?: ArchDesc | null;   // run's stored arch (ctrnnN: length doesn't identify it)
  motors?: number[] | null;       // best organism's steady-state motor output (shown at the motor nodes)
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const metaRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !genome) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    // Match the viewBox aspect to the container so the diagram FILLS the (wide) window
    // horizontally instead of letterboxing.
    const wrap = svg.parentElement;
    const cw = wrap?.clientWidth || 480, ch = wrap?.clientHeight || 360;
    const vbW = Math.max(480, Math.round(360 * (cw / ch)));
    let meta: string;
    // Prefer the run's stored descriptor (the only reliable source for ctrnnN);
    // else look it up by genome length in /api/topology; else solve ctrnn1.
    const desc = descriptor ?? topology?.archs[String(genome.length)];
    if (desc && desc.name !== "ctrnn1" && desc.layers.length >= 3) {
      meta = drawLayered(svg, genome, desc, vbW, motors);
    } else if (topology) {
      meta = drawCtrnn1(svg, genome, topology.input, topology.output);
    } else {
      meta = drawCtrnn1(svg, genome, 6, 4);
    }
    if (metaRef.current) metaRef.current.textContent = meta;
  }, [genome, topology, descriptor, motors]);
  return (
    <div className="net">
      <div className="viz-wrap">
        {!genome && <div className="viz-empty">waiting for a genome…</div>}
        <svg ref={svgRef} className="net-svg" />
      </div>
      <div className="net-meta dim mono"><span ref={metaRef} /></div>
    </div>
  );
}
